import React from 'react';
import { Navigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { useFeatures } from '../contexts/FeaturesContext';

/**
 * Garde du kiosque de pointage.
 *
 * Le kiosque vit hors de `AdministrationLayout` — c'est une page plein écran
 * posée sur une tablette à l'entrée de l'école — et sa route n'exigeait qu'une
 * session : n'importe quel employé connecté l'atteignait, même dans une école
 * qui n'a pas acheté le pointage. La permission `POINTAGES.KIOSK.VIEW` existe,
 * s'affiche dans le gestionnaire de rôles, et ne commandait rien.
 *
 * Deux conditions, dans cet ordre — comme `ProtectedRoute`, pour que le message
 * envoie au bon interlocuteur : d'abord la licence (« votre école n'a pas cette
 * extension »), ensuite le droit (« votre administrateur ne vous l'a pas
 * donné »).
 *
 *   1. licence `cap.rfid` — le kiosque est le seul usage des badges, et
 *      l'extension dépend déjà de `module.pointage` au catalogue du CIK : une
 *      école qui l'a souscrite a forcément le pointage.
 *   2. `POINTAGES.KIOSK.VIEW` OU l'accès au module `POINTAGES`.
 *
 * Le second point n'est pas de la générosité : il reprend mot pour mot ce
 * qu'exige la route serveur `POST /api/employes/verify-badge`. Un écran plus
 * strict que son API refuserait des gens que le serveur accepte — et un compte
 * kiosque qui fonctionne aujourd'hui doit continuer demain.
 *
 * ⚠️ Cet écran évite une page cassée, il ne protège pas la donnée : c'est le
 * serveur qui refuse.
 */

const Plein = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
    <div className="max-w-md w-full text-center">{children}</div>
  </div>
);

const KiosqueGuard = ({ children }) => {
  const { hasPermission, hasGranularPermission, loading } = usePermissions();
  const { estActive, chargement, libelle } = useFeatures();

  if (loading || chargement) {
    return (
      <Plein>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#246990] mx-auto" />
        <p className="mt-4 text-gray-600">Vérification des permissions...</p>
      </Plein>
    );
  }

  if (!estActive('cap.rfid')) {
    return (
      <Plein>
        <div className="mx-auto w-16 h-16 rounded-full bg-[#246990]/10 flex items-center justify-center mb-6">
          <Lock size={28} className="text-[#246990]" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">
          Module non inclus dans votre licence
        </h2>
        <p className="text-gray-600 leading-relaxed">
          Le module <strong>{libelle('cap.rfid')}</strong> n'est pas activé pour
          votre établissement. Rapprochez-vous de votre administrateur pour faire
          évoluer votre abonnement.
        </p>
      </Plein>
    );
  }

  const autorise = hasGranularPermission('POINTAGES', 'KIOSK', 'VIEW')
    || hasPermission('POINTAGES');

  if (!autorise) {
    return <Navigate to="/access-denied" replace />;
  }

  return children;
};

export default KiosqueGuard;
